import { ShieldCheck, Camera, CheckCircle } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import FadeIn from "../components/FadeIn";
import SEO from "../components/SEO";

const STEPS = [
  { icon: ShieldCheck, label: "ID Check" },
  { icon: Camera, label: "Selfie Match" },
  { icon: CheckCircle, label: "Approved" },
];

export default function VerificationPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="verification-page">
      <SEO title="Verification" path="/verification" description="Every HAEVN member is verified through ID check and selfie match. Real people only. No exceptions." />
      <Navigation />

      {/* Hero */}
      <section className="pt-24 pb-6 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-6xl text-haevn-navy leading-tight mb-4">
              Built for real people
            </h1>
            <p className="text-lg text-gray-500 font-body max-w-2xl">
              HAEVN uses verification to ensure real people and authentic profiles.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Verification is built in */}
      <section className="py-14 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h2 className="font-headline text-2xl md:text-3xl text-haevn-navy mb-4">
              Verification is built into the network
            </h2>
            <div className="max-w-2xl space-y-3">
              <p className="text-base text-gray-600 font-body leading-relaxed">
                Every profile on HAEVN goes through a verification process before entering the network.
              </p>
              <p className="text-base text-gray-500 font-body leading-relaxed">
                This reduces fake accounts, misrepresentation, and uncertainty — so you can focus on real connection.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* How verification works */}
      <section className="py-14 px-6 md:px-12 bg-[#F3F2F0]">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h2 className="font-headline text-2xl md:text-3xl text-haevn-navy mb-4">
              How verification works
            </h2>
            <div className="max-w-2xl space-y-3 mb-10">
              <p className="text-base text-gray-600 font-body leading-relaxed">
                We use a third-party verification system (Verif) to confirm identity.
              </p>
              <ul className="space-y-2 text-base text-gray-600 font-body">
                <li className="flex items-start gap-2">
                  <span className="text-haevn-teal mt-1.5 text-xs">&#9679;</span>
                  Government ID is securely verified
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-haevn-teal mt-1.5 text-xs">&#9679;</span>
                  A live selfie confirms you match your ID
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-haevn-teal mt-1.5 text-xs">&#9679;</span>
                  Profiles are approved before entering the network
                </li>
              </ul>
              <p className="text-sm text-gray-400 font-body">
                Verification happens once and is handled securely.
              </p>
            </div>
          </FadeIn>

          {/* 3-step diagram */}
          <FadeIn delay={100}>
            <div className="flex items-center justify-center gap-0 max-w-md mx-auto">
              {STEPS.map((step, i) => (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className="w-14 h-14 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                      <step.icon className="w-6 h-6 text-haevn-teal" />
                    </div>
                    <span className="text-xs font-subhead text-haevn-navy mt-2">{step.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="w-12 h-px bg-gray-300 mx-3 mb-5" />
                  )}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Why it matters */}
      <section className="py-14 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <h2 className="font-headline text-2xl md:text-3xl text-haevn-navy mb-4">
              Why it matters
            </h2>
            <div className="max-w-2xl space-y-3">
              <p className="text-base text-gray-600 font-body leading-relaxed">
                Most platforms treat trust as an afterthought.
                <br />HAEVN builds it into the system.
              </p>
              <p className="text-base text-gray-500 font-body leading-relaxed">
                You're not wondering if someone is real.
                <br />You're starting from a place of confidence.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Closing */}
      <section className="py-14 px-6 md:px-12 bg-[#F3F2F0]">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xl md:text-2xl text-haevn-navy font-headline leading-relaxed">
              Trust is part of the system, not an afterthought
            </p>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
